import { useMemo, useState } from "react";
import { Package, Search, ShieldX } from "lucide-react";
import { Link, Navigate } from "@tanstack/react-router";

import { useCurrentUser } from "~/lib/auth";
import {
    type ProductRecord,
    type ProductStatus,
    useCapabilities,
    useProducts,
} from "~/lib/products";

import { CreateProductDialog } from "~/components/CreateProductDialog";
import { TableSkeleton } from "~/components/TableSkeleton";

const STATUS_PILL: Record<ProductStatus, string> = {
    active: "status-pill--live",
    deprecated: "status-pill--paused",
    retired: "status-pill--failed",
};

const MAX_CAP_VISIBLE = 4;

export function ProductsPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const productsQ = useProducts();
    const capsQ = useCapabilities();

    const [search, setSearch] = useState("");
    const [vendorFilter, setVendorFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState<"" | ProductStatus>("");

    const vendors = useMemo(() => {
        const set = new Set<string>();
        for (const p of productsQ.data ?? []) set.add(p.vendor);
        return Array.from(set).sort();
    }, [productsQ.data]);

    const capabilityLabel = useMemo(() => {
        const m = new Map<string, string>();
        for (const c of capsQ.data ?? []) m.set(c.slug, c.name);
        return m;
    }, [capsQ.data]);

    const filtered = useMemo(() => {
        const list = productsQ.data ?? [];
        const q = search.trim().toLowerCase();
        return list.filter((p) => {
            if (q && !p.name.toLowerCase().includes(q) && !p.slug.toLowerCase().includes(q)) return false;
            if (vendorFilter && p.vendor !== vendorFilter) return false;
            if (statusFilter && p.status !== statusFilter) return false;
            return true;
        });
    }, [productsQ.data, search, vendorFilter, statusFilter]);

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    const all = productsQ.data ?? [];
    const activeCount = all.filter((p) => p.status === "active").length;
    const deprecatedCount = all.filter((p) => p.status === "deprecated").length;
    const totalLicenses = all.reduce((sum, p) => sum + p.licenseCount, 0);

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon">
                                <Package size={14} aria-hidden />
                            </span>
                            Catalog
                        </span>
                        <h1 className="page-header__title">Products</h1>
                        <p className="page-header__description">
                            Register the products that consume IntelRoom data. Each product declares
                            the capabilities it supports; licenses issued against a product can only
                            grant IoC types that match those capabilities.
                        </p>
                    </div>
                    <div className="page-header__actions">
                        <CreateProductDialog />
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {all.length.toLocaleString()} total
                    </span>
                    <span
                        className="page-header__meta__item tabular-nums"
                        style={{ color: "var(--color-success)" }}
                    >
                        {activeCount} active
                    </span>
                    {deprecatedCount > 0 && (
                        <span className="page-header__meta__item tabular-nums">
                            {deprecatedCount} deprecated
                        </span>
                    )}
                    <span className="page-header__meta__item tabular-nums">
                        {totalLicenses.toLocaleString()} licenses issued
                    </span>
                </div>
            </header>

            <section className="section-card" aria-label="Products">
                <header className="section-card__header">
                    <div className="toolbar" style={{ flex: 1 }}>
                        <label className="toolbar__field" style={{ flex: 1, maxWidth: 320 }}>
                            <Search size={14} aria-hidden />
                            <input
                                className="input input--ghost"
                                placeholder="Search name or slug"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </label>
                        <select
                            className="input input--sm"
                            value={vendorFilter}
                            onChange={(e) => setVendorFilter(e.target.value)}
                            aria-label="Filter by vendor"
                        >
                            <option value="">All vendors</option>
                            {vendors.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))}
                        </select>
                        <select
                            className="input input--sm"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as "" | ProductStatus)}
                            aria-label="Filter by status"
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="deprecated">Deprecated</option>
                            <option value="retired">Retired</option>
                        </select>
                    </div>
                </header>
                {productsQ.isLoading && <TableSkeleton rows={4} columns={6} />}
                {productsQ.isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load products</div>
                        <div className="empty-state__body">
                            {productsQ.error instanceof Error ? productsQ.error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {productsQ.data && filtered.length === 0 && (
                    <div className="empty-state">
                        <Package size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">
                            {all.length === 0 ? "No products yet" : "No products match your filters"}
                        </div>
                        <div className="empty-state__body">
                            {all.length === 0
                                ? "Register the first product above."
                                : "Try clearing the search or filters."}
                        </div>
                    </div>
                )}
                {filtered.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Slug</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Vendor</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Model</th>
                                    <th>Capabilities</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap", width: 1 }}>Licenses</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p) => (
                                    <ProductRow
                                        key={p.slug}
                                        product={p}
                                        capabilityLabel={capabilityLabel}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

interface ProductRowProps {
    product: ProductRecord;
    capabilityLabel: Map<string, string>;
}

function ProductRow({ product: p, capabilityLabel }: ProductRowProps): React.ReactElement {
    const visible = p.capabilities.slice(0, MAX_CAP_VISIBLE);
    const overflow = p.capabilities.length - visible.length;
    return (
        <tr>
            <td className="text-13">
                <Link
                    to="/products/$slug"
                    params={{ slug: p.slug }}
                    className="link"
                    style={{ fontWeight: 500 }}
                >
                    {p.name}
                </Link>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <code className="type-mono-sm">{p.slug}</code>
            </td>
            <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                <span className="chip chip--subtle">{p.vendor}</span>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <span className="chip">{p.model}</span>
            </td>
            <td>
                <span className="cluster-xs" style={{ flexWrap: "wrap" }}>
                    {visible.map((c) => (
                        <span key={c} className="chip chip--subtle">
                            {capabilityLabel.get(c) ?? c}
                        </span>
                    ))}
                    {overflow > 0 && (
                        <span className="chip chip--subtle" title={p.capabilities.slice(MAX_CAP_VISIBLE).join(", ")}>
                            +{overflow}
                        </span>
                    )}
                    {p.capabilities.length === 0 && (
                        <span className="text-12 text-text-subtle">—</span>
                    )}
                </span>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <span className={`status-pill ${STATUS_PILL[p.status]}`}>{p.status}</span>
            </td>
            <td className="text-13 text-text-muted tabular" style={{ textAlign: "right" }}>
                {p.licenseCount}
            </td>
        </tr>
    );
}
